/**
 * LocalStorage helpers for the permission-prompt queue.
 *
 * Tracks per-prompt state:
 *  - whether the feature is already enabled (→ stop forever)
 *  - which attempt we're on (1-4)
 *  - timestamp of the last dismissal (for time-based re-prompts)
 *  - whether the user has reached the max attempts (→ stop forever)
 *
 * All reads/writes wrapped in try/catch for private-browsing / quota errors.
 */

import { storageSet, storageRemove } from '../platform/storage';

// ─── generic safe helpers ───

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  storageSet(key, value);
}

function safeRemove(key: string): void {
  storageRemove(key);
}

// ─── key builders ───

const PREFIX = 'mailander_prompt_';

function enabledKey(promptId: string) {
  return `${PREFIX}${promptId}_enabled`;
}
function attemptKey(promptId: string) {
  return `${PREFIX}${promptId}_attempt`;
}
function dismissedAtKey(promptId: string) {
  return `${PREFIX}${promptId}_dismissed_at`;
}
function shownThisSessionKey(promptId: string) {
  return `${PREFIX}${promptId}_shown_session`;
}
function sessionPromptShownKey() {
  return `${PREFIX}session_prompt_shown`;
}

// ─── public API ───

export const PromptQueueStorage = {
  /**
   * Whether the feature behind this prompt is already enabled
   * (e.g. push notifications were granted). If true → never show again.
   */
  isEnabled(promptId: string): boolean {
    return safeGet(enabledKey(promptId)) === 'true';
  },

  setEnabled(promptId: string, enabled: boolean): void {
    if (enabled) {
      safeSet(enabledKey(promptId), 'true');
    } else {
      safeRemove(enabledKey(promptId));
    }
  },

  /**
   * Current attempt number (1-based). Returns 0 if never attempted.
   */
  getAttempt(promptId: string): number {
    const v = safeGet(attemptKey(promptId));
    return v ? parseInt(v, 10) || 0 : 0;
  },

  setAttempt(promptId: string, attempt: number): void {
    safeSet(attemptKey(promptId), String(attempt));
  },

  /**
   * Epoch-ms of the last dismissal.
   */
  getDismissedAt(promptId: string): number | null {
    const v = safeGet(dismissedAtKey(promptId));
    return v ? parseInt(v, 10) || null : null;
  },

  setDismissedAt(promptId: string, ts: number): void {
    safeSet(dismissedAtKey(promptId), String(ts));
  },

  /**
   * Record a dismissal: bumps attempt count and stores the timestamp.
   */
  recordDismissal(promptId: string): void {
    const next = this.getAttempt(promptId) + 1;
    this.setAttempt(promptId, next);
    this.setDismissedAt(promptId, Date.now());
  },

  /**
   * Whether ANY prompt has already been shown this app session.
   * Uses sessionStorage so it resets when the PWA is closed/reopened.
   */
  hasShownPromptThisSession(): boolean {
    try {
      return sessionStorage.getItem(sessionPromptShownKey()) === 'true';
    } catch {
      return false;
    }
  },

  markPromptShownThisSession(): void {
    try {
      sessionStorage.setItem(sessionPromptShownKey(), 'true');
    } catch {
      // ignore
    }
  },

  /**
   * Whether this prompt has already exhausted all attempts (4 max).
   */
  isExhausted(promptId: string): boolean {
    return this.getAttempt(promptId) >= 4;
  },

  /**
   * Clear all data for a prompt (useful for dev/testing).
   */
  clear(promptId: string): void {
    safeRemove(enabledKey(promptId));
    safeRemove(attemptKey(promptId));
    safeRemove(dismissedAtKey(promptId));
    safeRemove(shownThisSessionKey(promptId));
  },
};
